import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Shield, Users, Heart, Star } from 'lucide-react';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const values = [
    {
      icon: Heart,
      title: "Passion for Craft",
      description: "Every piece we create is born from genuine passion for woodworking and artistic expression."
    },
    {
      icon: Star,
      title: "Excellence",
      description: "We never compromise on quality, using only the finest materials and techniques."
    },
    {
      icon: Users,
      title: "Customer Focus",
      description: "Your vision guides our craft. We work closely with clients to bring their dreams to life."
    },
    {
      icon: Shield,
      title: "Sustainability",
      description: "We source responsibly and create furniture built to last generations."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Consultation",
      description: "We discuss your vision, space, and preferences to create the perfect design."
    },
    {
      step: "02",
      title: "Design",
      description: "Our artists create detailed sketches and 3D renders for your approval."
    },
    {
      step: "03",
      title: "Crafting",
      description: "Master artisans handcraft your piece using traditional and modern techniques."
    },
    {
      step: "04",
      title: "Delivery",
      description: "We carefully deliver and install your furniture with white-glove service."
    }
  ];

  const team = [
    {
      name: "Marcus Rodriguez",
      role: "Master Craftsman & Founder",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      bio: "With 15 years of experience, Marcus founded ResinCraft Studio to merge traditional woodworking with innovative resin artistry."
    },
    {
      name: "Elena Chen",
      role: "Lead Designer",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
      bio: "Elena brings architectural precision to our designs, ensuring each piece is both beautiful and functional."
    },
    {
      name: "James Thompson",
      role: "Resin Specialist",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      bio: "James is our epoxy expert, creating stunning resin effects that capture light and imagination."
    }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-dark-900 to-dark-950 relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold font-serif text-white mb-6">
              Our <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Story</span>
            </h1>
            <p className="text-xl text-dark-200 max-w-3xl mx-auto leading-relaxed">
              Founded in 2018, ResinCraft Studio was born from a passion to create furniture that tells a story. 
              We believe every piece should be as unique as the person who owns it.
            </p>
          </motion.div>
        </div>
        
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-600/10 rounded-full blur-3xl"></div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={itemVariants}>
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-white mb-6">
                Crafting <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Tomorrow's</span> Heirlooms
              </h2>
              <p className="text-lg text-dark-300 mb-6 leading-relaxed">
                At ResinCraft Studio, we don't just make furniture – we create functional art that becomes 
                part of your family's story. Each piece is meticulously handcrafted using sustainable 
                materials and time-honored techniques.
              </p>
              <p className="text-lg text-dark-300 mb-8 leading-relaxed">
                Our unique approach combines the natural beauty of reclaimed wood with the endless 
                possibilities of epoxy resin, resulting in one-of-a-kind pieces that capture light, 
                movement, and imagination.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-accent-400 mb-2">500+</div>
                  <div className="text-dark-300 font-medium">Pieces Created</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-accent-400 mb-2">98%</div>
                  <div className="text-dark-300 font-medium">Client Satisfaction</div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop"
                  alt="Crafting process"
                  className="rounded-2xl"
                />
                <img
                  src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&h=300&fit=crop"
                  alt="Finished product"
                  className="rounded-2xl mt-8"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-dark-900/90 backdrop-blur-md rounded-2xl p-6 border border-accent-500/30">
                <div className="flex items-center space-x-4">
                  <Award className="h-8 w-8 text-accent-400" />
                  <div>
                    <div className="text-white font-semibold">Award Winning</div>
                    <div className="text-dark-300 text-sm">Best Furniture Design 2023</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-white mb-6">
                Our <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Values</span>
              </h2>
              <p className="text-lg text-dark-300 max-w-2xl mx-auto">
                These core values guide everything we do, from initial design to final delivery.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center group"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-dark-300 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-white mb-6">
                Our <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Process</span>
              </h2>
              <p className="text-lg text-dark-300 max-w-2xl mx-auto">
                From concept to completion, we ensure every step meets our exacting standards.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {process.map((step, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="relative"
                >
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full mb-6 text-white font-bold text-lg">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-dark-300 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  
                  {/* Connector Line */}
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-accent-400 to-transparent -z-10"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-white mb-6">
                Meet Our <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Team</span>
              </h2>
              <p className="text-lg text-dark-300 max-w-2xl mx-auto">
                The master craftspeople behind every ResinCraft Studio creation.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center group"
                >
                  <div className="relative mb-6">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-32 h-32 rounded-full mx-auto object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-accent-500/20 to-accent-600/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {member.name}
                  </h3>
                  <div className="text-accent-400 font-medium mb-4">
                    {member.role}
                  </div>
                  <p className="text-dark-300 leading-relaxed">
                    {member.bio}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
